import ICAL from 'ical.js';

// Category mapping based on event titles and keywords
const getCategoryFromTitle = (title) => {
  const lowerTitle = title.toLowerCase();

  // Travel/Transport
  if (lowerTitle.includes('leaving') || lowerTitle.includes('departure') || lowerTitle.includes('arriving')) {
    return 'Travel';
  }

  // Break/Social categories
  if (lowerTitle.includes('coffee') || lowerTitle.includes('break')) {
    return 'Break';
  }
  if (lowerTitle.includes('lunch') || lowerTitle.includes('breakfast') || lowerTitle.includes('dinner')) {
    return 'Break';
  }
  if (
    lowerTitle.includes('social') ||
    lowerTitle.includes('evening') ||
    lowerTitle.includes('game') ||
    lowerTitle.includes('quiz') ||
    lowerTitle.includes('karaoke') ||
    lowerTitle.includes('spikeball')
  ) {
    return 'Social Activity';
  }

  // Arrival/Setup/Organization
  if (lowerTitle.includes('arrival') || lowerTitle.includes('setup') || lowerTitle.includes('check')) {
    return 'Organization';
  }

  // Welcome/Overview
  if (lowerTitle.includes('welcome') || lowerTitle.includes('overview') || lowerTitle.includes('program overview')) {
    return 'Welcome';
  }

  // PhD related
  if (lowerTitle.includes('phd') || lowerTitle.includes('outline')) {
    return 'PhD Update';
  }

  // Presentations
  if (lowerTitle.includes('presentation') || lowerTitle.includes('pitch') || lowerTitle.includes('demo')) {
    return 'Presentation';
  }

  // Discussions
  if (
    lowerTitle.includes('discussion') ||
    lowerTitle.includes('panel') ||
    lowerTitle.includes('evolve') ||
    lowerTitle.includes('middleware')
  ) {
    return 'Discussion';
  }

  // Sessions/Workshops
  if (
    lowerTitle.includes('breakout') ||
    lowerTitle.includes('session') ||
    lowerTitle.includes('group') ||
    lowerTitle.includes('triads')
  ) {
    return 'Session';
  }

  // Skills/Tools
  if (
    lowerTitle.includes('skills') ||
    lowerTitle.includes('tools') ||
    lowerTitle.includes('cursor') ||
    lowerTitle.includes('full-stack') ||
    lowerTitle.includes('development')
  ) {
    return 'Skills & Tools';
  }

  // Research
  if (
    lowerTitle.includes('research') ||
    lowerTitle.includes('hackathon') ||
    lowerTitle.includes('collaborative') ||
    lowerTitle.includes('brainstorming') ||
    lowerTitle.includes('failure fest')
  ) {
    return 'Research';
  }

  // Culture/Vision
  if (lowerTitle.includes('culture') || lowerTitle.includes('roadmap') || lowerTitle.includes('vision')) {
    return 'Lab Culture';
  }

  // Closing/Reflections
  if (lowerTitle.includes('closing') || lowerTitle.includes('reflection') || lowerTitle.includes('two truths')) {
    return 'Closing';
  }

  // Walk/Exercise
  if (lowerTitle.includes('walk') && lowerTitle.includes('talk')) {
    return 'Walk & Talk';
  }

  // Default
  return 'Session';
};

// Extract discussion time from description
const extractDiscussionTime = (description, title) => {
  if (!description) return null;

  // Look for patterns like "5 min discussion", "7 min Q&A", etc.
  const discussionMatch = description.match(/(\d+)\s*min(?:utes?)?\s*(?:discussion|q&a|questions?)/i);
  if (discussionMatch) {
    return parseInt(discussionMatch[1]);
  }

  // Check title for discussion indicators
  if (title.toLowerCase().includes('discussion') || title.toLowerCase().includes('q&a')) {
    return 5; // default discussion time
  }

  return null;
};

// Parse presenter name from title
const extractPresenterFromTitle = (title, description = '') => {
  // If the title seems to be a person's name (contains common name patterns)
  const namePattern = /^[A-Z][a-z]+\s+[A-Z][a-z]+/;
  if (namePattern.test(title)) {
    return title;
  }

  // Otherwise use the title as the session name
  return title;
};

// Calculate duration between start and end times
const calculateDuration = (startTime, endTime) => {
  const duration = (endTime.getTime() - startTime.getTime()) / (1000 * 60); // minutes
  return Math.round(duration);
};

// Parse ICS content
export const parseICSContent = (icsContent) => {
  try {
    const jcalData = ICAL.parse(icsContent);
    const comp = new ICAL.Component(jcalData);
    const vevents = comp.getAllSubcomponents('vevent');

    const events = vevents.map((vevent) => {
      const event = new ICAL.Event(vevent);

      const startTime = event.startDate.toJSDate();
      const endTime = event.endDate.toJSDate();
      const title = event.summary;
      const description = event.description || '';
      const location = event.location || '';

      // Format date and time
      const date = startTime.toISOString().split('T')[0];
      const time = startTime.toTimeString().slice(0, 5);

      // Calculate total duration
      const totalDuration = calculateDuration(startTime, endTime);

      // Extract discussion time
      const discussionTime = extractDiscussionTime(description, title);

      // Get category and presenter
      const category = getCategoryFromTitle(title);
      const presenter = extractPresenterFromTitle(title, description);

      const scheduleItem = {
        date,
        time,
        presenter,
        category,
        duration: totalDuration,
        location: location || undefined,
        description: description || undefined,
        uid: event.uid || undefined,
      };

      // Only add discussion if it exists
      if (discussionTime) {
        scheduleItem.discussion = discussionTime;
      }

      return scheduleItem;
    });

    // Sort by date and time
    events.sort((a, b) => {
      const dateTimeA = new Date(`${a.date}T${a.time}`);
      const dateTimeB = new Date(`${b.date}T${b.time}`);
      return dateTimeA - dateTimeB;
    });

    return { schedule: events };
  } catch (error) {
    console.error('Error parsing ICS content:', error);
    throw new Error('Failed to parse ICS calendar data');
  }
};

// Fetch and parse ICS from URL
export const fetchAndParseICS = async (icsUrl) => {
  const fetchMethods = [
    // Method 1: Try backend proxy first (production-ready)
    async () => {
      const proxyEndpoint = '/api/proxy-ics';
      const response = await fetch(proxyEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ url: icsUrl }),
      });

      if (!response.ok) {
        throw new Error(`Backend proxy failed: ${response.status} - ${response.statusText}`);
      }

      const data = await response.json();
      return data.icsContent;
    },

    // Method 2: Direct fetch (works in development)
    async () => {
      const response = await fetch(icsUrl, {
        method: 'GET',
        headers: {
          Accept: 'text/calendar, text/plain, */*',
        },
        signal: AbortSignal.timeout(10000),
      });

      if (!response.ok) {
        throw new Error(`Direct fetch failed: ${response.status} - ${response.statusText}`);
      }

      return await response.text();
    },

    // Method 3: CORS proxies (fallback for development)
    async () => {
      const corsProxies = [
        `https://api.allorigins.win/get?url=${encodeURIComponent(icsUrl)}`,
        `https://corsproxy.io/?${encodeURIComponent(icsUrl)}`,
      ];

      for (const proxyUrl of corsProxies) {
        try {
          const response = await fetch(proxyUrl, {
            method: 'GET',
            headers: {
              Accept: 'text/calendar, text/plain, */*',
            },
            signal: AbortSignal.timeout(10000),
          });

          if (!response.ok) {
            throw new Error(`CORS proxy failed: ${response.status}`);
          }

          let icsContent;
          if (proxyUrl.includes('allorigins.win')) {
            const data = await response.json();
            icsContent = data.contents;
          } else {
            icsContent = await response.text();
          }

          if (!icsContent || !icsContent.includes('BEGIN:VCALENDAR')) {
            throw new Error('Invalid ICS response from proxy');
          }

          return icsContent;
        } catch (error) {
          console.warn('CORS proxy attempt failed:', error.message);
          continue;
        }
      }
      throw new Error('All CORS proxies failed');
    },
  ];

  let lastError = null;

  for (let i = 0; i < fetchMethods.length; i++) {
    try {
      console.log(`Trying ICS fetch method ${i + 1}/${fetchMethods.length}`);

      const icsContent = await fetchMethods[i]();

      if (!icsContent || icsContent.trim() === '') {
        throw new Error('Empty response received');
      }

      // Validate that it looks like ICS content
      if (!icsContent.includes('BEGIN:VCALENDAR')) {
        throw new Error('Response does not appear to be valid ICS calendar data');
      }

      console.log('Successfully fetched ICS data');
      return parseICSContent(icsContent);
    } catch (error) {
      console.warn(`Method ${i + 1} failed:`, error.message);
      lastError = error;

      // Continue to next method
      continue;
    }
  }

  // If all methods failed, provide helpful error message
  throw new Error(`Failed to fetch ICS calendar data after trying ${fetchMethods.length} methods. 
    Last error: ${lastError?.message || 'Unknown error'}. 
    
    Production troubleshooting:
    1. Verify the backend proxy endpoint '/api/proxy-ics' is available
    2. Check if the ICS URL is accessible from the server
    3. Ensure the Google Calendar is set to public
    4. Consider using the 'Test Lab Retreat' button for sample data
    5. Upload the ICS file directly as an alternative`);
};

// Validate schedule data format
export const validateScheduleData = (data) => {
  if (!data || !data.schedule || !Array.isArray(data.schedule)) {
    return false;
  }

  return data.schedule.every(
    (item) => item.date && item.time && item.presenter && item.category && typeof item.duration === 'number',
  );
};

// Convert local ICS file content to schedule format (for testing)
export const parseLocalICSFile = async (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const icsContent = e.target.result;
        const scheduleData = parseICSContent(icsContent);
        resolve(scheduleData);
      } catch (error) {
        reject(error);
      }
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsText(file);
  });
};
